import { omitBy } from 'lodash/fp';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { JsonParam, useQueryParams } from 'use-query-params';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { BaseFilterParams } from 'dashboard/lib/interfaces/BaseFilterParams';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { RootState } from 'dashboard/redux/store';
import { ApiOptions } from 'lib/clients/ApiClient';
import usePrevious from 'lib/hooks/usePrevious';
import { PaginationParams } from 'lib/interfaces/api';

interface TableQueryParams<FilterParams = BaseFilterParams> {
  filter?: FilterParams;
  sort?: SortParams;
  pageSize?: number;
  currentPage?: number;
}

const initialSortState: SortParams = {
  sort: 'updated_at',
  shouldSortDescending: true,
};

export interface TableState<FilterParams = BaseFilterParams> {
  apiOptions: ApiOptions;
  filter: FilterParams;
  sort: SortParams;
  pageSize: number;
  currentPage: number;
  setFilter: Dispatch<SetStateAction<FilterParams>>;
  setSort: Dispatch<SetStateAction<SortParams>>;
  setPageSize: Dispatch<SetStateAction<number>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

interface UseTableStateReturn<FilterParams = BaseFilterParams> {
  tableState: TableState<FilterParams>;
  paginationCallbacks: UseTableStateCallbacksReturn;
  paginationMeta: PaginationParams | undefined;
  setPaginationMeta: Dispatch<SetStateAction<PaginationParams | undefined>>;
}

function queryToApiOptions<FilterParams = BaseFilterParams>({
  filter,
  sort,
  pageSize,
  currentPage,
  environmentId,
  filtersToApiFilters = (filters) => filters as ApiOptions['filter'],
}: {
  filter: FilterParams;
  sort: SortParams;
  pageSize: number;
  currentPage: number;
  environmentId: string;
  filtersToApiFilters?: (filterParams: FilterParams) => ApiOptions['filter'];
}) {
  return {
    filter: {
      ...filtersToApiFilters(filter),
      'environment-id': environmentId,
    },
    page: {
      number: currentPage,
      size: pageSize,
    },
    ...(sort
      ? {
          sort: `${sort.shouldSortDescending ? '-' : ''}${sort.sort}`,
        }
      : {}),
  };
}

/*
 * @name useTableState
 *
 * A hook that handles syncing of sort/filter/pagination state and URL
 * query string, and wrapping of state update callbacks with sensible
 * default behavior.
 *
 * When run, this hook sets up two effects. The first runs only on mount and
 * syncs the sort/filter/pagination state to the URL. The second runs on
 * sort/filter/pagination state change and syncs the URL back to state.
 *
 * This hook also returns some optional callbacks that wrap action creators
 * with some sane defaults.
 */
export default function <FilterParams extends BaseFilterParams = BaseFilterParams>({
  key,
  defaultFilterParams,
  filtersToApiFilters,
}: {
  key?: string;
  defaultFilterParams?: FilterParams;
  filtersToApiFilters?: (filterParams: FilterParams) => ApiOptions['filter'];
}): UseTableStateReturn<FilterParams> {
  // Set up with values from URL
  const [query, setQuery] = useQueryParams(
    key != null
      ? { [key]: JsonParam }
      : {
          filter: JsonParam,
          sort: JsonParam,
          pageSize: JsonParam,
          currentPage: JsonParam,
        },
  );
  const keyedParams: TableQueryParams<FilterParams> =
    key != null ? (query as Record<string, TableQueryParams<FilterParams>>)[key] || {} : query;

  // We remove any `undefined` filter values first, because these are not encoded
  // into the URL when filter values are set. We also need to remove `undefined`
  // filter values from urlFilter because these will mirror filter when there are
  // no URL parameters.
  const [filter, setFilter] = useState<FilterParams>(
    omitBy((v) => v == null, keyedParams.filter || defaultFilterParams) as FilterParams,
  );
  const [sort, setSort] = useState(
    omitBy((v) => v == null, keyedParams.sort || initialSortState) as SortParams,
  );
  const [currentPage, setCurrentPage] = useState(keyedParams.currentPage || 1);
  const [pageSize, setPageSize] = useState(keyedParams.pageSize || DEFAULT_PAGE_SIZE);

  const [paginationMeta, setPaginationMeta] = useState<PaginationParams>();

  // Reset pagination when toggling sandbox / prod
  const currentEnvironmentId = useSelector<RootState, string>(
    (state) => state.currentEnvironment.currentEnvironment?.id,
  );
  const previousEnvironmentId = usePrevious(currentEnvironmentId);
  const [apiOptions, setApiOptions] = useState(
    queryToApiOptions({
      filter,
      sort,
      currentPage,
      pageSize,
      environmentId: currentEnvironmentId,
      filtersToApiFilters,
    }),
  );
  useEffect(() => {
    // only run this effect if environment is changed, don't run on mount / unmount
    if (previousEnvironmentId !== currentEnvironmentId && previousEnvironmentId != null)
      setCurrentPage(1);
  }, [previousEnvironmentId, currentEnvironmentId]);

  // This syncs pagination state to the URL query string.
  useEffect(() => {
    const newParams = { filter, sort, currentPage, pageSize };
    const keyedParams = key != null ? { [key]: newParams } : newParams;

    setQuery(keyedParams);
    setApiOptions(
      queryToApiOptions({
        filter,
        sort,
        currentPage,
        pageSize,
        environmentId: currentEnvironmentId,
        filtersToApiFilters,
      }),
    );
  }, [filter, sort, pageSize, currentPage, key, setQuery]);

  const paginationCallbacks = useTableStateCallbacks({
    setSort,
    setPageSize,
    setCurrentPage,
  });

  return {
    tableState: {
      filter,
      sort,
      currentPage,
      pageSize,
      apiOptions,
      setFilter,
      setSort,
      setCurrentPage,
      setPageSize,
    },
    paginationCallbacks,
    paginationMeta,
    setPaginationMeta,
  };
}

interface UseTableStateCallbacksArgs {
  setSort: Dispatch<SetStateAction<SortParams>>;
  setPageSize: Dispatch<SetStateAction<number>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export interface UseTableStateCallbacksReturn {
  handlePageLinkClick: (newPageNumber: number) => void;
  handlePageSizeClick: (newPageSize: number) => void;
  handleSortUpdate: (newSortParams: SortParams) => void;
}

/**
 * @name useTableStateCallbacks
 *
 * A hook that wraps common callbacks needed for sorting and
 * pagination. This increases consistency by always running page cleanup such
 * as resetting the page count when changing filters.
 */
function useTableStateCallbacks({
  setSort,
  setPageSize,
  setCurrentPage,
}: UseTableStateCallbacksArgs): UseTableStateCallbacksReturn {
  const handlePageLinkClick = useCallback(
    (newPageNumber: number) => {
      setCurrentPage(newPageNumber);
    },
    [setCurrentPage],
  );

  const handlePageSizeClick = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);

      // Send back to the first page to avoid situation where a higher page size does not
      // have current page number.
      setCurrentPage(1);
    },
    [setPageSize, setCurrentPage],
  );

  const handleSortUpdate = useCallback(
    (sortParams: SortParams) => {
      setSort(sortParams);
      setCurrentPage(1);
    },
    [setSort, setCurrentPage],
  );

  return {
    handlePageLinkClick,
    handlePageSizeClick,
    handleSortUpdate,
  };
}
